import React, { useEffect, useMemo } from "react";
import "./Scoreboard.css";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useDispatch, useSelector } from "react-redux";
import { getAIScoreboardCourses } from "Redux/CourseDetails";
import { syncUser } from "Redux/UserDetails";
import { useMsal } from "@azure/msal-react";
import { applyScoreboardRules } from "utils/foundationUnlock";
import { computeScore, computeLevel, computePoints } from "utils/scoreboard";
import ProgressGauge from "./ProgressGauge";

const ScoreboardContent = () => {
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const enterpriseId = activeAccount?.idTokenClaims?.enterpriseID;
  const name = activeAccount?.idTokenClaims?.name;
  const userLevelDes = activeAccount?.idTokenClaims?.jobFamilyDescription;
  const role = activeAccount?.idTokenClaims?.jobTitle;
  const userData = useSelector((state) => state.userDetails.userData?.[0]);
  const isEmpty = (value) =>
    value === null || value === undefined || value === "" || value === "NULL";
  const level = useMemo(() => {
    return isEmpty(userLevelDes) ? userData?.level : userLevelDes;
  }, [userLevelDes, userData?.level]);

  const { AIScoreboardCourses = [] } = useSelector(
    (state) => state.courseDetails,
  );
  const { scoreboardCoursesGroupedByModule, totalPointsPerModule } =
    useMemo(() => {
      return applyScoreboardRules({
        AIScoreboardCourses,
        userData,
      });
    }, [AIScoreboardCourses, userData]);
  const {
    score,
    percentile,
    currentLevel,
    nextLevel,
    completedPointsPerModule,
    nextModules,
  } = useMemo(() => {
    const score = computeScore(scoreboardCoursesGroupedByModule);

    const percentile = (score / 1000) * 2 * 100;
    const { currentLevel, nextLevel } = computeLevel(score);

    const completedPointsPerModule = computePoints(
      scoreboardCoursesGroupedByModule,
    );

    const moduleNames = Array.from(scoreboardCoursesGroupedByModule.keys());

    const nextModules = moduleNames
      .filter((moduleName) => {
        const total = totalPointsPerModule.get(moduleName) || 0;
        const completed = completedPointsPerModule.get(moduleName) || 0;
        return total > 0 && completed !== total;
      })
      .slice(0, 4);

    return {
      score,
      percentile,
      currentLevel,
      nextLevel,
      completedPointsPerModule,
      nextModules,
    };
  }, [scoreboardCoursesGroupedByModule, totalPointsPerModule]);

  useEffect(() => {
    if (!enterpriseId) return;

    dispatch(syncUser(enterpriseId, name, level, role));
    dispatch(getAIScoreboardCourses({ enterpriseId }));
  }, [enterpriseId, dispatch]);

  return (
    <>
      <div className="few-pages-banner-wrap">
        <img
          src={
            process.env.PUBLIC_URL + "/Assets/Scoreboard/scoreboard-banner.png"
          }
          // src={urls["bannerIgniteTalks"]}
          alt="Ignite banner"
          className="banner-img"
        />
        <div className="scoreboard-page-banner-box">
          <div className="scoreboard-banner-wrapper">
            <div className="scoreboard-banner-content">
              {/* <h1 className="scoreboard-banner-main-head">Hi {user.name}...</h1> */}
              <h1 className="scoreboard-banner-main-head">
                Here is your AI scoreboard
              </h1>
              <div className="banner-score-text">
                <div className="banner-sub-div">
                  <p>Your Score</p>
                  <h1>{score}</h1>
                  <p>out of 500</p>
                </div>
                <div className="banner-sub-div">
                  <p>Your Level</p>
                  <h1>{currentLevel}</h1>
                  <p>Next {nextLevel}</p>
                </div>
              </div>
            </div>
            <div className="banner-right">
              <ProgressGauge score={score} />
            </div>
          </div>
        </div>
      </div>
      <div className="scoreboard-milestones-wrapper">
        <div className="scoreboard-milestones">
          <div className="milestones-left">
            <img
              src={
                process.env.PUBLIC_URL +
                "/Assets/Scoreboard/milestone-icon-img.png"
              }
              // src={urls["bannerIgniteTalks"]}
              alt="Milestone Icon"
              className="milestone-img-icon"
            />
          </div>
          <div className="milestones-right">
            <h3>Your are now Ai Advance earn your next milestone</h3>
            <p>Complete AI-Powered Engineering to earn AI Native</p>
            <span>{percentile}% complete</span>
            <span className="AI-powered">AI powered Engineering</span>
            {/* <progress max={100} value={20}>20%</progress> */}
            <div className="progress-container">
              <div
                className="progress-bar"
                style={{ width: `${percentile}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      <div className="score-details-section">
        <div className="score-breakdown">
          <div className="breakdown-heading">
            <div className="heading-left">
              <h3>Module Score breakdown</h3>
              <p>Earned points learning module - {userData?.role} role</p>
            </div>
            <div className="heading-right">
              <TrendingUpIcon />
            </div>
          </div>
          <div className="breakdown-content">
            {Array.from(scoreboardCoursesGroupedByModule.entries()).map(
              ([moduleName, courses]) => {
                const totalPoints = totalPointsPerModule.get(moduleName) || 0;
                const completedPoints =
                  completedPointsPerModule.get(moduleName) || 0;

                return (
                  <div className="score-cards" key={moduleName}>
                    <div className="score-cards-details">
                      <h4>{moduleName}</h4>
                    </div>

                    <div className="score-cards-pts">
                      <p>
                        <img
                          src={
                            process.env.PUBLIC_URL +
                            "/Assets/Scoreboard/points-icon.png"
                          }
                          alt="Points"
                          className="pts-icon"
                        />
                      </p>

                      <p
                        id="breakdown-points"
                        style={{
                          backgroundColor:
                            completedPoints === totalPoints && totalPoints > 0
                              ? "black"
                              : "#B1B1B1",
                        }}
                      >
                        {totalPoints} PT
                      </p>
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>
        <div className="next-step-wrapper">
          <div className="next-score-step">
            <div className="next-heading">
              <h3>Next Step</h3>
              <p>Complete below modules to earn AI points</p>
            </div>
            <div className="next-content">
              {nextModules.map((moduleName) => {
                const totalPoints = totalPointsPerModule.get(moduleName) || 0;

                return (
                  <div
                    className="next-scorecards"
                    key={moduleName}
                    style={{
                      border: `1px solid #cc66ff`,
                      borderLeft: `3px solid #cc66ff`,
                    }}
                  >
                    <h4>{moduleName}</h4>
                    <span id="next-pts" style={{ color: "#a100ff" }}>
                      {totalPoints}
                    </span>
                    <span>Points</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScoreboardContent;
